import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../../utils/api';
import { formatInUserTime } from '../../utils/date';

const POLL_INTERVAL_MS = 5000;
const ORDER_DING_ENABLED_KEY = 'gng_ownerOrderDingEnabled';
const ORDER_DING_SESSION_INIT_KEY = 'gng_ownerOrderDingSessionInitialized';
const ORDER_DING_CHANGED_EVENT = 'gng-owner-ding-changed';

function formatPhone(value) {
  const digits = (value || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const TABS = [
  { key: 'waiting', label: 'Waiting', statuses: ['pending', 'paid'] },
  { key: 'cooking', label: 'Cooking', statuses: ['in_progress'] },
  { key: 'ready', label: 'Ready', statuses: ['done'] },
  { key: 'history', label: 'History', statuses: ['picked_up', 'refunded'] },
];

export default function OrderQueue() {
  const { getToken } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('waiting');
  const [dingEnabled, setDingEnabled] = useState(() => {
    if (sessionStorage.getItem(ORDER_DING_SESSION_INIT_KEY) !== '1') {
      localStorage.setItem(ORDER_DING_ENABLED_KEY, '0');
      sessionStorage.setItem(ORDER_DING_SESSION_INIT_KEY, '1');
    }
    return localStorage.getItem(ORDER_DING_ENABLED_KEY) === '1';
  });
  const fetchOrdersRef = useRef(null);

  const fetchOrders = useCallback(async () => {
    const res = await api('/api/owners/me/orders', { getToken });
    if (!res.ok) {
      if (res.status === 403) {
        setError('Not authorized');
        return;
      }
      setError('Failed to load orders');
      return;
    }
    const data = await res.json();
    const list = Array.isArray(data.orders) ? data.orders : [];
    setOrders(list);
    setError(null);
  }, [getToken]);

  fetchOrdersRef.current = fetchOrders;

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === ORDER_DING_ENABLED_KEY) {
        setDingEnabled(e.newValue === '1');
      }
    };
    const onDingChanged = (e) => {
      setDingEnabled(Boolean(e?.detail?.enabled));
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(ORDER_DING_CHANGED_EVENT, onDingChanged);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(ORDER_DING_CHANGED_EVENT, onDingChanged);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await fetchOrders();
      if (!cancelled) setLoading(false);
    })();
    const interval = setInterval(() => {
      if (fetchOrdersRef.current) fetchOrdersRef.current();
    }, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && fetchOrdersRef.current) fetchOrdersRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchOrders]);

  const toggleDing = () => {
    setDingEnabled((prev) => {
      const next = !prev;
      localStorage.setItem(ORDER_DING_ENABLED_KEY, next ? '1' : '0');
      window.dispatchEvent(new CustomEvent(ORDER_DING_CHANGED_EVENT, { detail: { enabled: next } }));
      return next;
    });
  };

  const norm = (s) => (typeof s === 'string' ? s.toLowerCase().trim() : '');

  const waitingCount = orders.filter((o) => ['pending', 'paid'].includes(norm(o.status))).length;

  const filteredOrders = orders.filter((o) => {
    const tab = TABS.find((t) => t.key === activeTab);
    return tab?.statuses.includes(norm(o.status));
  });

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-slate-600 dark:text-slate-400">Loading orders…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6">
        <p className="text-red-700 dark:text-red-300">{error}</p>
        <Link to="/owner/dashboard" className="mt-4 inline-block text-amber-600 hover:text-amber-700">← Back</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-[calc(7rem+env(safe-area-inset-bottom))]">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/owner/dashboard" className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mb-1 inline-block">
            ← Back to dashboard
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            Order queue
          </h1>
          {waitingCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 mt-1">
              {waitingCount} new
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleDing}
            className={`px-4 py-2 rounded-lg border text-sm font-medium min-h-[40px] ${
              dingEnabled
                ? 'border-emerald-400 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20'
                : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300'
            } hover:bg-slate-100 dark:hover:bg-slate-700`}
          >
            {dingEnabled ? '🔔 Sound on' : '🔕 Sound off'}
          </button>
          <button
            type="button"
            onClick={fetchOrders}
            className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-3 rounded-lg font-medium min-h-[48px] ${
              activeTab === tab.key
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {filteredOrders.length === 0 && (
          <p className="text-slate-500 dark:text-slate-400 py-8 text-center">
            No orders in this section.
          </p>
        )}
        {filteredOrders.map((order) => (
          <OrderCard key={order.id} order={order} onUpdate={fetchOrders} getToken={getToken} />
        ))}
      </div>
    </div>
  );
}

function OrderCard({ order, onUpdate, getToken }) {
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const updateStatus = async (status) => {
    setLoading(true);
    try {
      const res = await api(`/api/owners/me/orders/${order.id}/status`, {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      await onUpdate();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async () => {
    if (!window.confirm('Issue full refund for this order? This cannot be undone.')) return;
    setLoading(true);
    try {
      const res = await api(`/api/owners/me/orders/${order.id}/refund`, {
        getToken,
        method: 'POST',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      await onUpdate();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const canStart = ['pending', 'paid'].includes(order.status);
  const canMarkDone = order.status === 'in_progress';
  const canRefund = !['pending', 'refunded'].includes(order.status);

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
      <div className="flex justify-between items-start gap-4">
        <div>
          <button
            type="button"
            onClick={() => setDetailOpen(true)}
            className="font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline text-left"
          >
            Order #{order.id}
          </button>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
            {order.itemsSummary}
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {[order.customerEmail, order.customerPhone ? formatPhone(order.customerPhone) : null].filter(Boolean).join(' · ') || ''}
            {order.customerEmail || order.customerPhone ? ' · ' : ''}${(order.totalCents / 100).toFixed(2)}
          </p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
            {formatInUserTime(order.createdAt)}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          {canStart && (
            <button
              type="button"
              onClick={() => updateStatus('in_progress')}
              disabled={loading}
              className="px-5 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium min-h-[48px]"
            >
              {loading ? '…' : 'Start'}
            </button>
          )}
          {canMarkDone && (
            <button
              type="button"
              onClick={() => updateStatus('done')}
              disabled={loading}
              className="px-5 py-3 rounded-lg bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white font-medium min-h-[48px]"
            >
              {loading ? '…' : 'Done'}
            </button>
          )}
          {canRefund && (
            <button
              type="button"
              onClick={handleRefund}
              disabled={loading}
              className="px-5 py-3 rounded-lg border border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 font-medium min-h-[48px]"
            >
              {loading ? '…' : 'Refund'}
            </button>
          )}
          {order.status === 'refunded' && (
            <span className="px-3 py-2 rounded-lg bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 text-sm font-medium">
              Refunded
            </span>
          )}
        </div>
      </div>

      {detailOpen && (
        <OrderDetailModal
          orderId={order.id}
          getToken={getToken}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </div>
  );
}

function OrderDetailModal({ orderId, getToken, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/owners/me/orders/${orderId}`, { getToken });
        if (cancelled) return;
        if (!res.ok) {
          setError('Failed to load order');
          return;
        }
        const data = await res.json();
        setOrder(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load order');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId, getToken]);

  if (!order && !loading && !error) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" role="dialog" aria-modal="true" aria-labelledby="order-detail-title">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <h2 id="order-detail-title" className="text-lg font-bold text-slate-900 dark:text-slate-100">
            Order #{orderId}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading && <p className="text-slate-500 dark:text-slate-400">Loading order…</p>}
          {error && <p className="text-red-600 dark:text-red-400">{error}</p>}
          {order && (
            <>
              <div className="space-y-1 text-sm">
                <p className="text-slate-600 dark:text-slate-400">
                  <span className="font-medium text-slate-700 dark:text-slate-300">Status:</span> {order.status}
                </p>
                <p className="text-slate-600 dark:text-slate-400">
                  <span className="font-medium text-slate-700 dark:text-slate-300">Placed:</span> {formatInUserTime(order.createdAt)}
                </p>
                {order.customerEmail && (
                  <p className="text-slate-600 dark:text-slate-400">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Email:</span> {order.customerEmail}
                  </p>
                )}
                {order.customerPhone && (
                  <p className="text-slate-600 dark:text-slate-400">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Phone:</span> {formatPhone(order.customerPhone)}
                  </p>
                )}
                <p className="text-slate-600 dark:text-slate-400">
                  <span className="font-medium text-slate-700 dark:text-slate-300">Total:</span> ${(order.totalCents / 100).toFixed(2)}
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Order details</h3>
                <div className="space-y-3">
                  {order.items?.map((item) => (
                    <div key={item.id} className="rounded-lg border border-slate-200 dark:border-slate-600 p-3 bg-slate-50 dark:bg-slate-800/50">
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {item.name} × {item.quantity}
                      </p>
                      {item.modifiers?.length > 0 && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                          {item.modifiers.map((m) => `${m.groupName}: ${m.optionName}`).join(' · ')}
                        </p>
                      )}
                      {item.addons?.length > 0 && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                          Add-ons: {item.addons.map((a) => `${a.name} ($${Number(a.price).toFixed(2)})`).join(', ')}
                        </p>
                      )}
                      {item.notes && (
                        <p className="text-sm text-amber-700 dark:text-amber-300 mt-1 italic">"{item.notes}"</p>
                      )}
                      <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
                        ${(item.unitPriceCents * item.quantity / 100).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
